import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
    Menu,
    MenuItem,
    Toolbar,
    Button,
    CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
    LayoutDashboard,
    Users,
    LogOut,
    Settings,
    ShieldCheck,
    Bell,
    Search,
    CreditCard,
    Activity,
    UserCheck,
    MoreVertical,
    Image as ImageIcon,
    Trash2,
    Upload,
    FileText,
    Moon,
    Sun
} from 'lucide-react';
import { getMe } from '../../services/authService';
import { getBanners, uploadBanner, deleteBanner, Banner } from '../../services/bannerService';
import {
    getReferralDiscount,
    setReferralDiscount,
    getAdminUsers,
    updateStudentSubscriptionStatus,
    AdminPartnerDetailsResponse,
    AdminPartnerListItem,
    AdminUser,
    getAdminPartnerDetails,
    getAdminPartners
} from '../../services/adminService';
import { TOKEN_KEY, USER_ROLE_KEY } from '../../utils/constants';
import ContentSyndicationPanel from '../../components/admin/ContentSyndicationPanel';
import { useColorMode } from '../../context/ColorModeContext';
import SyndicationCredentialsSettings from '../../components/admin/SyndicationCredentialsSettings';

interface UserData {
    name: string;
    email: string;
    role: string;
}

const drawerWidth = 260;
const USERS_PAGE_SIZE = 50;
const PARTNERS_PAGE_SIZE = 20;

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { mode, toggleMode } = useColorMode();
    const isDark = mode === 'dark';
    const [user, setUser] = useState<UserData | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activeSection, setActiveSection] = useState('Overview');

    // Banner State
    const [banners, setBanners] = useState<Banner[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [bannerError, setBannerError] = useState<string | null>(null);

    // Settings State
    const [referralSettings, setReferralSettings] = useState({
        discountPercent: 10,
        partnerCommissionPercent: 10,
        promoCodesText: 'MATIYARI'
    });
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [settingsMessage, setSettingsMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    // Users State
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [usersTotalCount, setUsersTotalCount] = useState(0);
    const [usersSearchInput, setUsersSearchInput] = useState('');
    const [usersSearchQuery, setUsersSearchQuery] = useState('');
    const [usersMessage, setUsersMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
    const [subscriptionUpdatingUserId, setSubscriptionUpdatingUserId] = useState<string | null>(null);

    // Partners State
    const [partners, setPartners] = useState<AdminPartnerListItem[]>([]);
    const [isPartnersLoading, setIsPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState<string | null>(null);
    const [partnersPage, setPartnersPage] = useState(1);
    const [partnersTotalPages, setPartnersTotalPages] = useState(1);
    const [partnersTotalCount, setPartnersTotalCount] = useState(0);
    const [partnersSearchInput, setPartnersSearchInput] = useState('');
    const [partnersSearchQuery, setPartnersSearchQuery] = useState('');
    const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
    const [selectedPartnerDetail, setSelectedPartnerDetail] = useState<AdminPartnerDetailsResponse | null>(null);
    const [isPartnerDetailLoading, setIsPartnerDetailLoading] = useState(false);
    const [partnerDetailError, setPartnerDetailError] = useState<string | null>(null);

    const handleLogout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_ROLE_KEY);
        navigate('/login');
    }, [navigate]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userData = await getMe();
                if (userData) {
                    setUser({
                        name: userData.name || userData.user?.name || 'Admin',
                        email: userData.email || userData.user?.email || '',
                        role: userData.role || userData.user?.role || 'admin'
                    });
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                handleLogout();
            }
        };

        fetchUserData();
    }, [handleLogout]);

    const fetchUsers = async (page: number, search: string) => {
        setIsUsersLoading(true);
        setUsersError(null);
        try {
            const data = await getAdminUsers({
                page,
                limit: USERS_PAGE_SIZE,
                search
            });
            setUsers(data.users);
            setUsersPage(data.pagination.page);
            setUsersTotalPages(data.pagination.totalPages);
            setUsersTotalCount(data.pagination.total);
        } catch (err) {
            console.error('Failed to fetch users', err);
            setUsersError('Failed to load users');
        } finally {
            setIsUsersLoading(false);
        }
    };

    const fetchPartners = async (page: number, search: string) => {
        setIsPartnersLoading(true);
        setPartnersError(null);
        try {
            const data = await getAdminPartners({
                page,
                limit: PARTNERS_PAGE_SIZE,
                search,
            });
            setPartners(data.partners);
            setPartnersPage(data.pagination.page);
            setPartnersTotalPages(data.pagination.totalPages);
            setPartnersTotalCount(data.pagination.total);
        } catch (err) {
            console.error('Failed to fetch partners', err);
            setPartnersError('Failed to load partners');
        } finally {
            setIsPartnersLoading(false);
        }
    };

    const fetchPartnerDetails = async (partnerId: string) => {
        setIsPartnerDetailLoading(true);
        setPartnerDetailError(null);
        try {
            const detail = await getAdminPartnerDetails(partnerId);
            setSelectedPartnerDetail(detail);
        } catch (err) {
            console.error('Failed to fetch partner detail', err);
            setPartnerDetailError('Failed to load partner details');
            setSelectedPartnerDetail(null);
        } finally {
            setIsPartnerDetailLoading(false);
        }
    };

    useEffect(() => {
        if (activeSection === 'Banners') {
            fetchBanners();
        } else if (activeSection === 'Settings') {
            fetchSettings();
        } else if (activeSection === 'Users') {
            fetchUsers(usersPage, usersSearchQuery);
        } else if (activeSection === 'Partners') {
            fetchPartners(partnersPage, partnersSearchQuery);
        }
    }, [activeSection, usersPage, usersSearchQuery, partnersPage, partnersSearchQuery]);

    useEffect(() => {
        if (activeSection !== 'Partners') return;
        if (!selectedPartnerId) {
            setSelectedPartnerDetail(null);
            return;
        }
        fetchPartnerDetails(selectedPartnerId);
    }, [activeSection, selectedPartnerId]);

    useEffect(() => {
        if (activeSection !== 'Partners') return;
        if (partners.length === 0) {
            setSelectedPartnerId(null);
            setSelectedPartnerDetail(null);
            return;
        }
        const exists = selectedPartnerId && partners.some((partner) => partner.id === selectedPartnerId);
        if (!exists) {
            setSelectedPartnerId(partners[0].id);
        }
    }, [activeSection, partners, selectedPartnerId]);

    const fetchSettings = async () => {
        try {
            const data = await getReferralDiscount();
            setReferralSettings({
                discountPercent: data.discountPercent,
                partnerCommissionPercent: data.partnerCommissionPercent,
                promoCodesText: (data.promoCodes || ['MATIYARI']).join(', ')
            });
            setSettingsMessage(null);
        } catch (err) {
            console.error('Failed to fetch settings', err);
            setSettingsMessage({ text: 'Failed to load settings', type: 'error' });
        }
    };

    const handleSaveSettings = async () => {
        setIsSavingSettings(true);
        setSettingsMessage(null);
        try {
            const promoCodes = referralSettings.promoCodesText
                .split(/[\s,]+/)
                .map((code) => code.trim().toUpperCase().replace(/[^A-Z0-9-]/g, ''))
                .filter(Boolean);

            if (promoCodes.length === 0) {
                setSettingsMessage({ text: 'At least one promo code is required', type: 'error' });
                return;
            }

            const response = await setReferralDiscount(
                referralSettings.discountPercent,
                referralSettings.partnerCommissionPercent,
                promoCodes
            );
            if (response?.promoCodes) {
                setReferralSettings((prev) => ({ ...prev, promoCodesText: response.promoCodes.join(', ') }));
            }
            setSettingsMessage({ text: 'Settings saved successfully', type: 'success' });
        } catch (err) {
            console.error('Failed to save settings', err);
            setSettingsMessage({ text: 'Failed to save settings', type: 'error' });
        } finally {
            setIsSavingSettings(false);
        }
    };

    const fetchBanners = async () => {
        try {
            const data = await getBanners();
            setBanners(data);
            setBannerError(null);
        } catch (err) {
            console.error('Failed to fetch banners', err);
            setBannerError('Failed to load banners');
        }
    };

    const handleBannerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setIsUploading(true);
            setBannerError(null);
            try {
                const newBanner = await uploadBanner(event.target.files[0]);
                setBanners([newBanner, ...banners]);
            } catch (err) {
                console.error('Upload failed', err);
                setBannerError('Failed to upload banner');
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleBannerDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            try {
                await deleteBanner(id);
                setBanners(banners.filter(b => b._id !== id));
            } catch (err) {
                console.error('Delete failed', err);
                setBannerError('Failed to delete banner');
            }
        }
    };

    const handleUsersSearch = (event: React.FormEvent) => {
        event.preventDefault();
        setUsersMessage(null);
        const nextQuery = usersSearchInput.trim();
        if (usersPage !== 1) {
            setUsersPage(1);
        }
        if (usersSearchQuery !== nextQuery) {
            setUsersSearchQuery(nextQuery);
            return;
        }
        fetchUsers(1, nextQuery);
    };

    const handlePartnersSearch = (event: React.FormEvent) => {
        event.preventDefault();
        const nextQuery = partnersSearchInput.trim();

        if (partnersPage !== 1) {
            setPartnersPage(1);
        }

        if (partnersSearchQuery !== nextQuery) {
            setPartnersSearchQuery(nextQuery);
            return;
        }

        fetchPartners(1, nextQuery);
    };

    const handleUsersSubscriptionToggle = async (targetUser: AdminUser) => {
        if (targetUser.role !== 'student') return;

        const shouldSubscribe = targetUser.subscriptionStatus !== 'active';
        setSubscriptionUpdatingUserId(targetUser._id);
        setUsersMessage(null);
        try {
            const response = await updateStudentSubscriptionStatus(targetUser._id, shouldSubscribe);
            setUsers((prevUsers) =>
                prevUsers.map((item) =>
                    item._id === targetUser._id
                        ? { ...item, subscriptionStatus: response.user.subscriptionStatus }
                        : item
                )
            );
            setUsersMessage({
                text: `${targetUser.name} ${shouldSubscribe ? 'subscribed' : 'unsubscribed'} successfully`,
                type: 'success'
            });
        } catch (err) {
            console.error('Failed to update student subscription', err);
            setUsersMessage({
                text: `Failed to ${shouldSubscribe ? 'subscribe' : 'unsubscribe'} student`,
                type: 'error'
            });
        } finally {
            setSubscriptionUpdatingUserId(null);
        }
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const pageBg = theme.palette.background.default;
    const surfaceBg = theme.palette.background.paper;
    const borderColor = theme.palette.divider;
    const headingColor = theme.palette.text.primary;
    const mutedColor = theme.palette.text.secondary;
    const tableHeaderBg = alpha(theme.palette.text.primary, isDark ? 0.08 : 0.03);
    const cardShadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 4px 20px -5px rgba(0,0,0,0.05)';
    const sidebarBg = isDark ? '#020617' : '#1e293b';
    const sidebarMuted = alpha('#ffffff', 0.65);
    const sidebarHover = alpha('#ffffff', 0.08);
    const sidebarSelected = alpha(theme.palette.primary.main, 0.9);

    // Mock Data
    const stats = [
        { label: 'Total Users', value: '12,345', trend: '+12%', icon: Users, color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.16) },
        { label: 'Active Learners', value: '8,124', trend: '+5%', icon: UserCheck, color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.16) },
        { label: 'Partners', value: '145', trend: '+2%', icon: ShieldCheck, color: theme.palette.secondary.main, bg: alpha(theme.palette.secondary.main, 0.16) },
        { label: 'Total Revenue', value: '$45,230', trend: '+18%', icon: CreditCard, color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.16) },
    ];

    const recentActivity = [
        { id: 1, user: 'Rahul Sharma', action: 'New Registration', time: '2 mins ago', status: 'Success' },
        { id: 2, user: 'Amit Patel', action: 'Subscription Purchased', time: '15 mins ago', status: 'Completed' },
        { id: 3, user: 'Priya Singh', action: 'Mock Test Completed', time: '1 hour ago', status: 'Reviewed' },
        { id: 4, user: 'Vikram Malhotra', action: 'Partner Application', time: '3 hours ago', status: 'Pending' },
        { id: 5, user: 'Neha Gupta', action: 'Login from new device', time: '5 hours ago', status: 'Values' },
    ];
    const usersStartIndex = usersTotalCount === 0 ? 0 : (usersPage - 1) * USERS_PAGE_SIZE + 1;
    const usersEndIndex = Math.min(usersPage * USERS_PAGE_SIZE, usersTotalCount);
    const partnersStartIndex = partnersTotalCount === 0 ? 0 : (partnersPage - 1) * PARTNERS_PAGE_SIZE + 1;
    const partnersEndIndex = Math.min(partnersPage * PARTNERS_PAGE_SIZE, partnersTotalCount);
    const formatCurrency = (amount: number) =>
        `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');

    return (
        <Box sx={{ display: 'flex', bgcolor: pageBg, minHeight: '100vh' }}>
            {/* Sidebar Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        bgcolor: sidebarBg,
                        color: 'white',
                        borderRight: `1px solid ${alpha('#ffffff', 0.08)}`
                    },
                }}
            >
                <Toolbar sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShieldCheck color={theme.palette.primary.main} size={28} />
                    <Typography variant="h6" fontWeight={700} color="white">
                        ZapTick AI Admin
                    </Typography>
                </Toolbar>
                <Divider sx={{ borderColor: alpha('#ffffff', 0.12) }} />

                <Box sx={{ overflow: 'auto', mt: 2, px: 2 }}>
                    <List>
                        {['Overview', 'Users', 'Partners', 'Analytics', 'Banners', 'Syndication', 'Settings'].map((text, index) => {
                            const Icon = [LayoutDashboard, Users, UserCheck, Activity, ImageIcon, FileText, Settings][index];
                            return (
                                <ListItem key={text} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        selected={activeSection === text || (text === 'Settings' && activeSection === 'Syndication Credentials')}
                                        onClick={() => setActiveSection(text)}
                                        sx={{
                                            borderRadius: 2,
                                            '&.Mui-selected': {
                                                bgcolor: sidebarSelected,
                                                '&:hover': { bgcolor: sidebarSelected }
                                            },
                                            '&:hover': { bgcolor: sidebarHover }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40, color: activeSection === text ? 'white' : sidebarMuted }}>
                                            <Icon size={20} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={text}
                                            primaryTypographyProps={{
                                                fontSize: '0.95rem',
                                                fontWeight: activeSection === text ? 600 : 500,
                                                color: activeSection === text ? 'white' : sidebarMuted
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>

                <Box sx={{ mt: 'auto', p: 2 }}>
                    <ListItemButton
                        onClick={toggleMode}
                        sx={{
                            borderRadius: 2,
                            color: '#e2e8f0',
                            mb: 1,
                            '&:hover': { bgcolor: sidebarHover }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 40, color: '#e2e8f0' }}>
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </ListItemIcon>
                        <ListItemText
                            primary={isDark ? 'Light Mode' : 'Dark Mode'}
                            primaryTypographyProps={{ fontWeight: 600 }}
                        />
                    </ListItemButton>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            borderRadius: 2,
                            color: theme.palette.error.light,
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 40, color: theme.palette.error.light }}>
                            <LogOut size={20} />
                        </ListItemIcon>
                        <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600 }} />
                    </ListItemButton>
                </Box>
            </Drawer>

            {/* Main Content */}
            <Box component="main" sx={{ flexGrow: 1, p: 4, ml: 0 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={800} color={headingColor}>
                            Dashboard
                        </Typography>
                        <Typography variant="body1" color={mutedColor}>
                            Welcome back, {user?.name}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Paper
                            component="form"
                            sx={{
                                p: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                width: 250,
                                borderRadius: 2,
                                boxShadow: 'none',
                                border: `1px solid ${borderColor}`,
                                bgcolor: surfaceBg
                            }}
                        >
                            <Search size={18} color={mutedColor} />
                            <Box
                                component="input"
                                placeholder="Search..."
                                sx={{
                                    ml: 1,
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    fontSize: '0.9rem',
                                    color: headingColor,
                                    bgcolor: 'transparent',
                                    '&::placeholder': { color: mutedColor, opacity: 1 }
                                }}
                            />
                        </Paper>
                        <Avatar sx={{ bgcolor: surfaceBg, color: headingColor, border: `1px solid ${borderColor}`, cursor: 'pointer' }}>
                            <Bell size={20} />
                        </Avatar>
                        <Avatar
                            sx={{ bgcolor: theme.palette.primary.main, fontWeight: 700, cursor: 'pointer' }}
                            onClick={handleMenu}
                        >
                            {user?.name.charAt(0) || 'A'}
                        </Avatar>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={handleClose}>Profile</MenuItem>
                            <MenuItem onClick={handleClose}>Settings</MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ color: 'red' }}>Logout</MenuItem>
                        </Menu>
                    </Stack>
                </Box>

                {/* Overview Section */}
                {activeSection === 'Overview' && (
                    <Box>
                        {/* Stats Grid */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                            {stats.map((stat, index) => (
                                <Box key={index} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', lg: 'calc(25% - 18px)' } }}>
                                    <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}` }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: stat.bg, color: stat.color }}>
                                                    <stat.icon size={24} />
                                                </Box>
                                                <Chip
                                                    label={stat.trend}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha(theme.palette.success.main, 0.18),
                                                        color: theme.palette.success.main,
                                                        fontWeight: 700,
                                                        borderRadius: 1.5,
                                                        height: 24
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="h4" fontWeight={700} color={headingColor}>
                                                {stat.value}
                                            </Typography>
                                            <Typography variant="body2" color={mutedColor} fontWeight={500}>
                                                {stat.label}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>
                            ))}
                        </Box>

                        {/* Recent Activity Section */}
                        <Typography variant="h6" fontWeight={700} color={headingColor} sx={{ mb: 2 }}>
                            Recent Activity
                        </Typography>
                        <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>User</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Action</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Time</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recentActivity.map((row) => (
                                            <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.text.primary, 0.12), fontSize: '0.8rem', color: mutedColor, fontWeight: 600 }}>
                                                            {row.user.charAt(0)}
                                                        </Avatar>
                                                        <Typography variant="subtitle2" fontWeight={600} color={headingColor}>
                                                            {row.user}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{row.action}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.status}
                                                        size="small"
                                                        sx={{
                                                            borderRadius: 1.5,
                                                            fontWeight: 600,
                                                            bgcolor: row.status === 'Success' || row.status === 'Completed' ? alpha(theme.palette.success.main, 0.18) :
                                                                row.status === 'Pending' ? alpha(theme.palette.warning.main, 0.2) : alpha(theme.palette.text.primary, 0.12),
                                                            color: row.status === 'Success' || row.status === 'Completed' ? theme.palette.success.main :
                                                                row.status === 'Pending' ? theme.palette.warning.main : mutedColor
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ color: mutedColor }}>{row.time}</TableCell>
                                                <TableCell align="right">
                                                    <MoreVertical size={16} color={mutedColor} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </Box>
                )}

                {/* Users Section */}
                {activeSection === 'Users' && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>All Users</Typography>
                                <Typography variant="body2" color={mutedColor}>
                                    New users are shown first. Showing {usersTotalCount} users in total.
                                </Typography>
                            </Box>

                            <Paper
                                component="form"
                                onSubmit={handleUsersSearch}
                                sx={{
                                    p: '6px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: { xs: '100%', md: 420 },
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: `1px solid ${borderColor}`,
                                    bgcolor: surfaceBg,
                                    gap: 1
                                }}
                            >
                                <Search size={18} color={mutedColor} />
                                <Box
                                    component="input"
                                    value={usersSearchInput}
                                    onChange={(e) => setUsersSearchInput(e.target.value)}
                                    placeholder="Search by name, email or role"
                                    sx={{
                                        ml: 1,
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        fontSize: '0.9rem',
                                        color: headingColor,
                                        bgcolor: 'transparent',
                                        '&::placeholder': { color: mutedColor, opacity: 1 }
                                    }}
                                />
                                <Button type="submit" variant="contained" size="small">Search</Button>
                                {usersSearchQuery && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={() => {
                                            setUsersSearchInput('');
                                            setUsersSearchQuery('');
                                            setUsersPage(1);
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Paper>
                        </Box>

                        {usersMessage && (
                            <Box sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: 2,
                                bgcolor: usersMessage.type === 'success'
                                    ? alpha(theme.palette.success.main, 0.16)
                                    : alpha(theme.palette.error.main, 0.18),
                                color: usersMessage.type === 'success' ? theme.palette.success.main : theme.palette.error.main,
                                border: `1px solid ${usersMessage.type === 'success'
                                    ? alpha(theme.palette.success.main, 0.35)
                                    : alpha(theme.palette.error.main, 0.35)}`
                            }}>
                                {usersMessage.text}
                            </Box>
                        )}

                        {usersError && (
                            <Typography color="error" sx={{ mb: 2 }}>{usersError}</Typography>
                        )}

                        <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 900 }}>
                                    <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Role</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Subscription</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Joined</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: mutedColor }}>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isUsersLoading && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                                    <CircularProgress size={28} />
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {!isUsersLoading && users.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 5, color: mutedColor }}>
                                                    No users found
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {!isUsersLoading && users.map((row) => {
                                            const fullName = `${row.name || ''} ${row.lastName || ''}`.trim() || 'N/A';
                                            const isStudent = row.role === 'student';
                                            const isSubscribed = row.subscriptionStatus === 'active';
                                            const isUpdating = subscriptionUpdatingUserId === row._id;
                                            return (
                                                <TableRow key={row._id}>
                                                    <TableCell>
                                                        <Typography variant="subtitle2" fontWeight={600} color={headingColor}>
                                                            {fullName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{row.email || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={row.role}
                                                            size="small"
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                fontWeight: 600,
                                                                bgcolor: row.role === 'admin'
                                                                    ? alpha(theme.palette.primary.main, 0.18)
                                                                    : row.role === 'student'
                                                                        ? alpha(theme.palette.success.main, 0.18)
                                                                        : alpha(theme.palette.text.primary, 0.12),
                                                                color: row.role === 'admin'
                                                                    ? theme.palette.primary.main
                                                                    : row.role === 'student'
                                                                        ? theme.palette.success.main
                                                                        : mutedColor
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={isStudent ? (isSubscribed ? 'Active' : 'Free') : 'N/A'}
                                                            size="small"
                                                            sx={{
                                                                borderRadius: 1.5,
                                                                fontWeight: 600,
                                                                bgcolor: isStudent
                                                                    ? (isSubscribed ? alpha(theme.palette.success.main, 0.18) : alpha(theme.palette.text.primary, 0.12))
                                                                    : alpha(theme.palette.text.primary, 0.12),
                                                                color: isStudent ? (isSubscribed ? theme.palette.success.main : mutedColor) : mutedColor
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ color: mutedColor }}>
                                                        {new Date(row.createdAt).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {isStudent ? (
                                                            <Button
                                                                variant={isSubscribed ? 'outlined' : 'contained'}
                                                                color={isSubscribed ? 'warning' : 'primary'}
                                                                size="small"
                                                                disabled={isUpdating}
                                                                onClick={() => handleUsersSubscriptionToggle(row)}
                                                            >
                                                                {isUpdating ? 'Updating...' : (isSubscribed ? 'Unsubscribe' : 'Subscribe')}
                                                            </Button>
                                                        ) : (
                                                            <Typography variant="body2" color={mutedColor}>-</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="body2" color={mutedColor}>
                                Showing {usersStartIndex}-{usersEndIndex} of {usersTotalCount}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={usersPage <= 1 || isUsersLoading}
                                    onClick={() => setUsersPage((prev) => Math.max(prev - 1, 1))}
                                >
                                    Previous
                                </Button>
                                <Typography variant="body2" color={mutedColor}>
                                    Page {usersPage} of {Math.max(usersTotalPages, 1)}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={usersPage >= usersTotalPages || isUsersLoading}
                                    onClick={() => setUsersPage((prev) => Math.min(prev + 1, usersTotalPages))}
                                >
                                    Next
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                )}

                {/* Partners Section */}
                {activeSection === 'Partners' && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>Enrollment Partners</Typography>
                                <Typography variant="body2" color={mutedColor}>
                                    EP users, referral usage, earning and withdrawal analytics.
                                </Typography>
                            </Box>

                            <Paper
                                component="form"
                                onSubmit={handlePartnersSearch}
                                sx={{
                                    p: '6px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: { xs: '100%', md: 420 },
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: `1px solid ${borderColor}`,
                                    bgcolor: surfaceBg,
                                    gap: 1
                                }}
                            >
                                <Search size={18} color={mutedColor} />
                                <Box
                                    component="input"
                                    value={partnersSearchInput}
                                    onChange={(e) => setPartnersSearchInput(e.target.value)}
                                    placeholder="Search partner by name or email"
                                    sx={{
                                        ml: 1,
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        fontSize: '0.9rem',
                                        color: headingColor,
                                        bgcolor: 'transparent',
                                        '&::placeholder': { color: mutedColor, opacity: 1 }
                                    }}
                                />
                                <Button type="submit" variant="contained" size="small">Search</Button>
                                {partnersSearchQuery && (
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={() => {
                                            setPartnersSearchInput('');
                                            setPartnersSearchQuery('');
                                            setPartnersPage(1);
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Paper>
                        </Box>

                        {partnersError && (
                            <Typography color="error" sx={{ mb: 2 }}>{partnersError}</Typography>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 1fr' }, gap: 3 }}>
                            <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}`, overflow: 'hidden' }}>
                                <TableContainer>
                                    <Table sx={{ minWidth: 820 }}>
                                        <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Partner</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Referred Users</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Code Uses</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Earned</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Available</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600, color: mutedColor }}>View</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {isPartnersLoading && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                                        <CircularProgress size={28} />
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {!isPartnersLoading && partners.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: mutedColor }}>
                                                        No partners found
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {!isPartnersLoading && partners.map((partner) => {
                                                const selected = selectedPartnerId === partner.id;
                                                return (
                                                    <TableRow
                                                        key={partner.id}
                                                        hover
                                                        selected={selected}
                                                        onClick={() => setSelectedPartnerId(partner.id)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell>
                                                            <Typography variant="subtitle2" fontWeight={600} color={headingColor}>
                                                                {partner.fullName}
                                                            </Typography>
                                                            <Typography variant="caption" color={mutedColor}>
                                                                {partner.email}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{partner.codeStats.totalUsersReferred}</TableCell>
                                                        <TableCell>{partner.codeStats.totalCodeUses}</TableCell>
                                                        <TableCell>{formatCurrency(partner.codeStats.totalEarned)}</TableCell>
                                                        <TableCell>{formatCurrency(partner.balance.available)}</TableCell>
                                                        <TableCell align="right">
                                                            <Button
                                                                size="small"
                                                                variant={selected ? 'contained' : 'outlined'}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setSelectedPartnerId(partner.id);
                                                                }}
                                                            >
                                                                {selected ? 'Selected' : 'Open'}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Box sx={{ p: 2, borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" color={mutedColor}>
                                        Showing {partnersStartIndex}-{partnersEndIndex} of {partnersTotalCount}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={partnersPage <= 1 || isPartnersLoading}
                                            onClick={() => setPartnersPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            Previous
                                        </Button>
                                        <Typography variant="body2" color={mutedColor}>
                                            Page {partnersPage} of {Math.max(partnersTotalPages, 1)}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={partnersPage >= partnersTotalPages || isPartnersLoading}
                                            onClick={() => setPartnersPage((prev) => Math.min(prev + 1, partnersTotalPages))}
                                        >
                                            Next
                                        </Button>
                                    </Stack>
                                </Box>
                            </Card>

                            <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}` }}>
                                <CardContent sx={{ p: 3 }}>
                                    {!selectedPartnerId && (
                                        <Typography color={mutedColor}>Select a partner from the left table to view details.</Typography>
                                    )}

                                    {selectedPartnerId && isPartnerDetailLoading && (
                                        <Box sx={{ py: 6, textAlign: 'center' }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    )}

                                    {selectedPartnerId && partnerDetailError && (
                                        <Typography color="error">{partnerDetailError}</Typography>
                                    )}

                                    {selectedPartnerDetail && !isPartnerDetailLoading && !partnerDetailError && (
                                        <Box>
                                            <Typography variant="h6" fontWeight={700}>
                                                {selectedPartnerDetail.partner.fullName}
                                            </Typography>
                                            <Typography variant="body2" color={mutedColor} sx={{ mb: 2 }}>
                                                {selectedPartnerDetail.partner.email}
                                            </Typography>

                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                                                <Card sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${borderColor}` }}>
                                                    <CardContent sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color={mutedColor}>Users Referred</Typography>
                                                        <Typography fontWeight={700}>{selectedPartnerDetail.summary.totalUsersReferred}</Typography>
                                                    </CardContent>
                                                </Card>
                                                <Card sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${borderColor}` }}>
                                                    <CardContent sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color={mutedColor}>Total Earned</Typography>
                                                        <Typography fontWeight={700}>{formatCurrency(selectedPartnerDetail.summary.totalEarned)}</Typography>
                                                    </CardContent>
                                                </Card>
                                                <Card sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${borderColor}` }}>
                                                    <CardContent sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color={mutedColor}>Withdrawn</Typography>
                                                        <Typography fontWeight={700}>{formatCurrency(selectedPartnerDetail.summary.totalWithdrawn)}</Typography>
                                                    </CardContent>
                                                </Card>
                                                <Card sx={{ borderRadius: 2, boxShadow: 'none', border: `1px solid ${borderColor}` }}>
                                                    <CardContent sx={{ p: 1.5 }}>
                                                        <Typography variant="caption" color={mutedColor}>Available</Typography>
                                                        <Typography fontWeight={700}>{formatCurrency(selectedPartnerDetail.summary.availableBalance)}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Box>

                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Referral Codes</Typography>
                                            <Box sx={{ maxHeight: 180, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: 2, mb: 2 }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Code</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Uses</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Earned</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {selectedPartnerDetail.codes.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center" sx={{ color: mutedColor }}>
                                                                    No referral codes
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        {selectedPartnerDetail.codes.map((code) => (
                                                            <TableRow key={code.id}>
                                                                <TableCell>{code.code}</TableCell>
                                                                <TableCell>{code.usageCount}</TableCell>
                                                                <TableCell>{formatCurrency(code.totalEarned)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>

                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Recent Code Uses</Typography>
                                            <Box sx={{ maxHeight: 180, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: 2, mb: 2 }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Code</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>User</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Commission</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {selectedPartnerDetail.recentUsages.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center" sx={{ color: mutedColor }}>
                                                                    No usage yet
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        {selectedPartnerDetail.recentUsages.slice(0, 20).map((usage, idx) => (
                                                            <TableRow key={`${usage.code}-${idx}`}>
                                                                <TableCell>{usage.code}</TableCell>
                                                                <TableCell>{usage.user?.name || 'Unknown User'}</TableCell>
                                                                <TableCell>{formatCurrency(usage.commission)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>

                                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Withdrawal History</Typography>
                                            <Box sx={{ maxHeight: 180, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: 2 }}>
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: tableHeaderBg }}>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Amount</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Status</TableCell>
                                                            <TableCell sx={{ fontWeight: 600, color: mutedColor }}>Date</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {selectedPartnerDetail.withdrawals.length === 0 && (
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center" sx={{ color: mutedColor }}>
                                                                    No withdrawal requests
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                        {selectedPartnerDetail.withdrawals.slice(0, 20).map((request) => (
                                                            <TableRow key={request.id}>
                                                                <TableCell>{formatCurrency(request.amount)}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={request.status}
                                                                        color={request.status === 'approved' ? 'success' : request.status === 'pending' ? 'warning' : request.status === 'rejected' ? 'error' : 'default'}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{formatDateTime(request.createdAt)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                )}

                {/* Banner Management Section */}
                {activeSection === 'Banners' && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h5" fontWeight={700}>Landing Page Banners</Typography>
                            <Box>
                                <input
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                    type="file"
                                    onChange={handleBannerUpload}
                                    disabled={isUploading}
                                />
                                <label htmlFor="raised-button-file">
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        px: 3,
                                        py: 1.5,
                                        borderRadius: 2,
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        opacity: isUploading ? 0.7 : 1
                                    }}>
                                        {isUploading ? <Activity className="animate-spin" size={20} /> : <Upload size={20} />}
                                        <Typography fontWeight={600}>
                                            {isUploading ? 'Uploading...' : 'Upload Banner'}
                                        </Typography>
                                    </Box>
                                </label>
                            </Box>
                        </Box>

                        {bannerError && (
                            <Typography color="error" sx={{ mb: 2 }}>{bannerError}</Typography>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
                            {banners.map((banner) => (
                                <Card key={banner._id} sx={{ position: 'relative', overflow: 'hidden', borderRadius: 3 }}>
                                    <Box component="img" src={banner.imageUrl} alt="Banner" sx={{ width: '100%', height: 200, objectFit: 'cover' }} />
                                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Added: {new Date(banner.createdAt).toLocaleDateString()}
                                        </Typography>
                                        <Box
                                            onClick={() => handleBannerDelete(banner._id)}
                                            sx={{
                                                p: 1,
                                                borderRadius: '50%',
                                                bgcolor: alpha(theme.palette.error.main, 0.16),
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.24) }
                                            }}
                                        >
                                            <Trash2 size={20} color={theme.palette.error.main} />
                                        </Box>
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                        {banners.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 8, color: mutedColor }}>
                                <ImageIcon size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                                <Typography>No banners uploaded yet</Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Settings Section */}
                {activeSection === 'Syndication' && (
                    <ContentSyndicationPanel onOpenCredentialsSettings={() => setActiveSection('Syndication Credentials')} />
                )}

                {activeSection === 'Syndication Credentials' && (
                    <SyndicationCredentialsSettings onBack={() => setActiveSection('Settings')} />
                )}

                {/* Settings Section */}
                {activeSection === 'Settings' && (
                    <Box>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Global Settings</Typography>
                            <Typography variant="body2" color="text.secondary">Configure application-wide settings and parameters</Typography>
                        </Box>

                        <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}`, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                    Syndication Credentials
                                </Typography>
                                <Typography variant="body2" color={mutedColor} sx={{ mb: 2 }}>
                                    Sabhi blog/backlink platforms ke API tokens, usernames aur keys manage karne ke liye dedicated page.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => setActiveSection('Syndication Credentials')}
                                >
                                    Open Credentials Page
                                </Button>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: cardShadow, border: `1px solid ${borderColor}` }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>Referral Program Settings</Typography>

                                {settingsMessage && (
                                    <Box sx={{
                                        p: 2,
                                        mb: 3,
                                        borderRadius: 2,
                                        bgcolor: settingsMessage.type === 'success'
                                            ? alpha(theme.palette.success.main, 0.16)
                                            : alpha(theme.palette.error.main, 0.18),
                                        color: settingsMessage.type === 'success' ? theme.palette.success.main : theme.palette.error.main,
                                        border: `1px solid ${settingsMessage.type === 'success'
                                            ? alpha(theme.palette.success.main, 0.35)
                                            : alpha(theme.palette.error.main, 0.35)}`
                                    }}>
                                        {settingsMessage.text}
                                    </Box>
                                )}

                                <Box sx={{ maxWidth: 400 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: headingColor }}>
                                        Student Discount Percentage (%)
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, color: mutedColor }}>
                                        This is the discount applied to the subscription when a student uses an enrollment partner's referral code.
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                                        <input
                                            type="number"
                                            value={referralSettings.discountPercent}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val) && val >= 0 && val <= 100) {
                                                    setReferralSettings({ ...referralSettings, discountPercent: val });
                                                } else if (e.target.value === '') {
                                                    setReferralSettings({ ...referralSettings, discountPercent: 0 }); // fallback to allow clearing
                                                }
                                            }}
                                            min="0"
                                            max="100"
                                            style={{
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: `1px solid ${borderColor}`,
                                                backgroundColor: surfaceBg,
                                                color: headingColor,
                                                fontSize: '16px',
                                                width: '120px',
                                                outline: 'none',
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: headingColor }}>
                                        Partner Commission Percentage (%)
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, color: mutedColor }}>
                                        This is the commission percentage that enrollment partners receive from successful subscriptions using their code.
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                                        <input
                                            type="number"
                                            value={referralSettings.partnerCommissionPercent}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (!isNaN(val) && val >= 0 && val <= 100) {
                                                    setReferralSettings({ ...referralSettings, partnerCommissionPercent: val });
                                                } else if (e.target.value === '') {
                                                    setReferralSettings({ ...referralSettings, partnerCommissionPercent: 0 }); // fallback to allow clearing
                                                }
                                            }}
                                            min="0"
                                            max="100"
                                            style={{
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: `1px solid ${borderColor}`,
                                                backgroundColor: surfaceBg,
                                                color: headingColor,
                                                fontSize: '16px',
                                                width: '120px',
                                                outline: 'none',
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: headingColor }}>
                                        Special Subscription Promo Codes
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2, color: mutedColor }}>
                                        In codes ko student website ya app me enter karne par 1-month free special subscription card open hoga.
                                    </Typography>

                                    <Box
                                        component="textarea"
                                        value={referralSettings.promoCodesText}
                                        onChange={(e) => {
                                            setReferralSettings({
                                                ...referralSettings,
                                                promoCodesText: e.target.value.toUpperCase().replace(/[^A-Z0-9,\-\s]/g, '')
                                            });
                                        }}
                                        placeholder="MATIYARI, INDER, HIRA, DEOMANI"
                                        sx={{
                                            width: '100%',
                                            minHeight: 92,
                                            p: '10px 14px',
                                            mb: 3,
                                            borderRadius: '8px',
                                            border: `1px solid ${borderColor}`,
                                            backgroundColor: surfaceBg,
                                            color: headingColor,
                                            fontSize: '16px',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            outline: 'none',
                                        }}
                                    />

                                    <Box sx={{ mt: 2 }}>
                                        <button
                                            onClick={handleSaveSettings}
                                            disabled={isSavingSettings}
                                            style={{
                                                padding: '10px 24px',
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                cursor: isSavingSettings ? 'not-allowed' : 'pointer',
                                                opacity: isSavingSettings ? 0.7 : 1,
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {isSavingSettings ? 'Saving...' : 'Save Config'}
                                        </button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default AdminDashboard;
